import {
  Attribute,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  Self,
  ViewChild,
} from '@angular/core';
import {
  ControlValueAccessor,
  FormControl,
  FormControlDirective,
  FormControlName,
  FormGroupDirective,
  NgControl,
  NgModel,
} from '@angular/forms';
import { MatOptionSelectionChange } from '@angular/material/core';
import { MatSelect } from '@angular/material/select';
import { Subject, takeUntil } from 'rxjs';

interface Option {
  value: string;
  label: string;
  icon: string;
  lowerCaseLabel: string;
  canRemove: boolean;
  forceOpenOption?: boolean;
}

@Component({
  selector: 'searchable-select',
  templateUrl: './searchable-select.component.html',
  styleUrls: ['./searchable-select.component.scss'],
})
export class SearchableSelectComponent<T> implements OnInit, OnDestroy, OnChanges, ControlValueAccessor {
  multiple: boolean;
  allowClear: boolean;
  alphabetical: boolean;
  hideSearch: boolean;
  includeAddButton: boolean;
  includeRemoveButton: boolean;

  // Normally a select won't open if it has no options, this will ensure it can be opened
  forceAllowOpen: boolean;

  // Observer for the disabled state, to pass it through to the child element
  // disabled=true is used for permissions directive
  observer: MutationObserver;
  isDisabled: boolean = false;

  constructor(
    private el: ElementRef,
    @Optional() @Self() public ngControl: NgControl,
    @Optional() @Attribute('multiple') multiple: any,
    @Optional() @Attribute('allowClear') allowClear: any,
    @Optional() @Attribute('alphabetical') alphabetical: any,
    @Optional() @Attribute('hideSearch') hideSearch: any,
    @Optional() @Attribute('includeAddButton') includeAddButton: any,
    @Optional() @Attribute('includeRemoveButton') includeRemoveButton: any,
    @Optional() @Attribute('forceAllowOpen') forceAllowOpen: any,
  ) {
    if (ngControl != null) {
      ngControl.valueAccessor = this;
    }
    this.multiple = multiple !== undefined && multiple !== null;
    this.allowClear = allowClear !== undefined && allowClear !== null;
    this.alphabetical = alphabetical !== undefined && alphabetical !== null;
    this.hideSearch = hideSearch !== undefined && hideSearch !== null;
    this.includeAddButton = includeAddButton !== undefined && includeAddButton !== null;
    this.includeRemoveButton = includeRemoveButton !== undefined && includeRemoveButton !== null;
    this.forceAllowOpen = forceAllowOpen !== undefined && forceAllowOpen !== null;

    this.observer = new MutationObserver((list) =>
      list.filter((mut) => mut.target).forEach((mut) => (this.isDisabled = !!mut.target['disabled'])),
    );
    this.observer.observe(this.el.nativeElement, {
      attributes: true,
      attributeFilter: ['disabled'],
      childList: false,
      subtree: false,
    });
  }

  @ViewChild('searchControl', { static: true })
  searchControl: ElementRef;
  @ViewChild(MatSelect)
  selectComponent: MatSelect;

  control: FormControl;

  // Just passing the control through, so don't need to implement these
  writeValue(obj: any): void {}
  registerOnChange(fn: any): void {}
  registerOnTouched(fn: any): void {}
  // This is used in the taskdrawer, improve how this works
  setDisabledState?(isDisabled: boolean): void {}

  // TODO: Probably move this once it's stable and works reasonably well

  unsubscribe$: Subject<void> = new Subject();

  @Input()
  placeholder: string = 'Search...';
  @Input()
  label: string;
  @Input()
  cssClass: string;
  @Input()
  panelClass: string = '';
  @Input()
  appearance: string = 'outline';

  @Input()
  options: T[];
  @Input()
  labelProperty: string | null;
  @Input()
  valueProperty: string | null;
  @Input()
  iconProperty: string | null;
  @Input()
  canRemoveProperty: string | null;
  @Input()
  clipboardDetailsProperty: string | null;

  @Input()
  sortFn: (a: T, b: T) => number;

  @Input()
  noMatchMessage = 'No results found...';

  @Output()
  add = new EventEmitter<string>();
  @Output()
  remove = new EventEmitter<any>();
  @Output()
  clipboard = new EventEmitter<any>();
  @Output() focus = new EventEmitter<FocusEvent>();
  @Output() blur = new EventEmitter<FocusEvent>();

  selectedLabels: string[];

  transformedOptions: Option[];
  filteredOptions: Option[];

  searchText = '';
  noMatches = false;
  canAdd = false;

  // Pass through some select methods
  open(): void {
    this.selectComponent?.open();
  }
  close(): void {
    this.selectComponent?.close();
  }
  toggle(): void {
    this.selectComponent?.toggle();
  }

  // Reset search, and focus on the search control when opened
  openedChange(open: boolean): void {
    this.searchText = '';
    this.searchTextChanged(!open);

    if (open) {
      this.searchControl.nativeElement.focus();
    }
  }

  // Clear the selection
  clear(event: UIEvent): void {
    this.control.setValue(this.control.defaultValue);
    event.stopPropagation();
  }

  // Filters options
  searchTextChanged(includeForceAllowOpen?: boolean): void {
    const text = this.searchText.toLowerCase();
    let result = this.transformedOptions;

    if (!includeForceAllowOpen) {
      result = result.filter((opt) => !opt.forceOpenOption);
    }

    if (text) {
      result = result.filter((opt) => opt.lowerCaseLabel.includes(text));
    }

    this.noMatches = result.length === 0;
    this.canAdd = !!text.trim() && !result.find((opt) => opt.lowerCaseLabel === text);
    this.filteredOptions = result;
  }

  // Prevents entering space in the search box from selecting an option
  onSpaceKeydown(event: KeyboardEvent): void {
    event.stopPropagation();
  }

  // Ensure that selected values are maintained when a value is selected while the list is filtered
  onSelectionChange(event: MatOptionSelectionChange): void {
    if (event.isUserInput) {
      if (this.multiple) {
        let values: any[] = this.control.value || [];
        if (event.source.selected) {
          if (!values.includes(event.source.value)) {
            values.push(event.source.value);
          }
        } else {
          if (values.includes(event.source.value)) {
            values = values.filter((v) => v !== event.source.value);
          }
        }

        this.control.setValue(values);

        // Re-focus on the search box
        // TODO: Figure out a better way to do this, don't like the timeout
        setTimeout(() => this.searchControl.nativeElement.focus(), 0);
      } else {
        this.control.setValue(event.source.selected ? event.source.value : null);
      }
    }
  }

  private updateSelectedLabels(): void {
    let values = this.control.value;
    if (!Array.isArray(values)) {
      values = [values];
    }
    const lookup = Object.fromEntries(
      this.transformedOptions
        .filter((opt) => !opt.forceOpenOption)
        .map((opt) => [JSON.stringify(opt.value), opt.label]),
    );
    this.selectedLabels = values.map((v) => lookup[JSON.stringify(v)] || '');
  }
  private init(): void {
    this.unsubscribe$.next();

    this.selectedLabels = null;

    const valueProp = this.valueProperty || this.labelProperty || null;
    const labelProp = this.labelProperty || this.valueProperty || null;
    const iconProp = this.iconProperty || null;
    const canRemoveProp = this.canRemoveProperty || null;
    const clipboardDetailsProp = this.clipboardDetailsProperty || null;

    const sortedOptions = [...(this.options || [])];
    if (this.sortFn) {
      sortedOptions.sort(this.sortFn);
    }

    this.transformedOptions = sortedOptions
      .map((opt) => ({
        value: valueProp ? opt?.[valueProp] : opt?.toString(),
        label: (labelProp ? opt?.[labelProp]?.toString() : opt?.toString()) || '',
        icon: (iconProp ? opt?.[iconProp] : null) || null,
        canRemove: canRemoveProp ? !!opt?.[canRemoveProp] : false,
        clipboardDetails: clipboardDetailsProp ? opt?.[clipboardDetailsProp] : false,
      }))
      .map((opt) => ({
        ...opt,
        lowerCaseLabel: opt.label.toLowerCase(),
      }));

    if (this.forceAllowOpen) {
      this.transformedOptions.push({
        value: null,
        label: '',
        icon: null,
        lowerCaseLabel: '',
        canRemove: false,
        forceOpenOption: true,
      });
    }

    if (this.alphabetical) {
      this.transformedOptions.sort((a, b) => a.label.localeCompare(b.label));
    }

    this.filteredOptions = this.transformedOptions;
    this.noMatches = this.filteredOptions.length === 0;

    // https://stackoverflow.com/a/58551853
    if (this.ngControl instanceof FormControlName) {
      const formGroupDirective = this.ngControl.formDirective as FormGroupDirective;
      if (formGroupDirective) {
        this.control = formGroupDirective.form.controls[this.ngControl.name] as FormControl;
      }
    } else if (this.ngControl instanceof FormControlDirective) {
      this.control = this.ngControl.control;
    } else if (this.ngControl instanceof NgModel) {
      this.control = this.ngControl.control;
      this.control.valueChanges
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe(() => this.ngControl.viewToModelUpdate(this.control.value));
    } else if (!this.ngControl) {
      this.control = new FormControl();
    }

    // Ensure the labels of the multiselect stay up to date
    //if (this.multiple) {
    this.updateSelectedLabels();
    this.control.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe(() => this.updateSelectedLabels());
    //}

    // Apply filter if there's already text in the search box
    // This will happen if the input options list is changed while it's open
    if (this.searchText) {
      this.searchTextChanged();
    }
  }

  compareWith(o1: any, o2: any): boolean {
    return o1 === o2 || JSON.stringify(o1) === JSON.stringify(o2);
  }

  addItem(name: string, event: PointerEvent): void {
    this.add.emit(name);
    event.stopPropagation();
  }
  removeItem(item: any, event: PointerEvent): void {
    this.remove.emit(item);
    event.stopPropagation();
  }
  clipboardCopied: string | null = null;
  copyToClipboard(item: any, event: PointerEvent): void {
    if (item.clipboardDetails) {
      navigator.clipboard.writeText(item.clipboardDetails).then(() => {
        this.clipboardCopied = item.clipboardDetails;
        this.clipboard.emit(item);
        setTimeout(() => (this.clipboardCopied = null), 3000);
      });
    }
    event.stopPropagation();
  }

  ngOnInit(): void {
    this.init();
  }
  ngOnChanges(): void {
    this.init();
  }
  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    this.observer.disconnect();
  }
}
